import PageHeader from "@/components/page-header";
import { CheckPaymentSuccessDialog } from "@/components/registrant/check-payment-success-dialog";
import { ContextHelpButton } from "@/components/registrant/help/context-help-button";
import { PeriodsAndFeesForm } from "@/components/registrant/license-form/periods-and-fees";
import { toast } from "@/components/ui/sonner";
import {
  getLicenseDetailsOptions,
  getLicenseTypeQuestionsOptions,
  updateLicenseAdditionalQuestionsMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import type {
  LicenseFeesResponseSchema,
  LicenseTypeConfigurationResponseSchema,
  QuestionResponseSchema,
} from "@/hera-api/client/types.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { useLicenseFormMutations } from "@/hooks/use-license-form-mutations";
import { useLicensePaymentData } from "@/hooks/use-license-payment-data";
import { useStepManager } from "@/hooks/use-step-manager";
import { invalidateRegistrantDashboardLists } from "@/lib/registrant-dashboard-query-invalidation";
import { getQueryErrorMessage } from "@/lib/utils";
import type { LicenseAnswers, PeriodsAndFeesData } from "@/types/license";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/registrant/licenses/$licenseId/payment")(
  {
    component: PaymentScreen,
    validateSearch: (search: Record<string, unknown>) => {
      return {
        paymentMethod:
          (search.paymentMethod as
            | "card"
            | "us_bank_account"
            | "check"
            | "invoice") || undefined,
      };
    },
  },
);

interface PaymentPeriodsAndFeesFormProps {
  onPrevious: () => Promise<void>;
  onSubmit: (
    data: PeriodsAndFeesData,
    paymentMethodType?:
      | "card"
      | "us_bank_account"
      | "check"
      | "invoice"
      | "pay_later"
      | "no_payment",
  ) => Promise<void>;
  stepNumber: number;
  isRenewal: boolean;
  feesData: LicenseFeesResponseSchema;
  configurationData: LicenseTypeConfigurationResponseSchema;
  initialPaymentMethod?:
    | "card"
    | "us_bank_account"
    | "check"
    | "invoice"
    | null;
  paymentScreenQuestions?: QuestionResponseSchema[];
  paymentScreenAnswers?: Record<string, unknown> | null;
}

function PaymentPeriodsAndFeesForm({
  onSubmit,
  stepNumber,
  isRenewal,
  feesData,
  configurationData,
  initialPaymentMethod,
  effectiveDateLabel,
  paymentScreenQuestions,
  paymentScreenAnswers,
}: Omit<PaymentPeriodsAndFeesFormProps, "onPrevious"> & {
  effectiveDateLabel?: string;
}) {
  return (
    <PeriodsAndFeesForm
      onSubmit={onSubmit}
      stepNumber={stepNumber}
      isRenewal={isRenewal}
      feesData={feesData}
      configurationData={configurationData}
      initialPaymentMethod={initialPaymentMethod}
      effectiveDateLabel={effectiveDateLabel}
      paymentScreenQuestions={paymentScreenQuestions}
      paymentScreenAnswers={paymentScreenAnswers}
    />
  );
}

export function PaymentScreen() {
  const { licenseId } = Route.useParams();
  const { paymentMethod: searchPaymentMethod } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const [checkPaymentSuccess, setCheckPaymentSuccess] = useState<{
    displayId: string;
    checkMailingAddress?: string | null;
    variant: "check" | "invoice";
  } | null>(null);

  const { stepNumber } = useStepManager({
    maxSteps: 1,
  });

  // Get license details
  const {
    data: licenseData,
    isLoading: isQueryLoading,
    error: queryError,
  } = useQuery({
    ...getLicenseDetailsOptions({
      path: {
        license_id: licenseId,
      },
    }),
    enabled: isAuthSetupComplete && !!licenseId,
  });

  // Get form data using payment-specific hook
  const {
    feesData,
    configurationData,
    isLoading: isFormDataLoading,
    error: formDataError,
  } = useLicensePaymentData({
    licenseId,
    licenseTypeId: licenseData?.license_type.id,
    jurisdictionId: licenseData?.jurisdiction.id,
  });

  const { data: questionsData } = useQuery({
    ...getLicenseTypeQuestionsOptions({
      path: {
        license_type_id: licenseData?.license_type.id ?? "",
        jurisdiction_id: licenseData?.jurisdiction.id ?? "",
      },
    }),
    enabled:
      isAuthSetupComplete &&
      !!licenseData?.license_type.id &&
      !!licenseData?.jurisdiction.id,
  });

  // Use custom mutations hook
  const {
    handleCreatePendingPayments,
    handleCreateCheckPayment,
    handleCreateInvoicePayment,
    handleMakePayment,
    processLicenseAnswers,
  } = useLicenseFormMutations({
    onSuccess: async () => {
      // Payment will be handled in handleSubmit
    },
  });

  const updateAnswersMutation = useMutation({
    ...updateLicenseAdditionalQuestionsMutation(),
  });

  // Handle form submission
  const handleSubmit = async (
    data: PeriodsAndFeesData,
    paymentMethodType?:
      | "card"
      | "us_bank_account"
      | "check"
      | "invoice"
      | "pay_later"
      | "no_payment",
  ) => {
    if (!licenseData?.id) {
      toast({
        title: "Error",
        description: "Unable to get license information",
        variant: "destructive",
      });
      return;
    }

    // Filter only selected periods and convert to the correct format
    const selectedPeriods = data.periods
      .filter((period) => period.selected)
      .map((period) => ({
        selected: period.selected,
        start_date: period.start_date,
        end_date: period.end_date,
        fee: period.fee,
        late_fee: period.late_fee,
        prefilled_fee: period.prefilled_fee,
        prefilled_late_fee: period.prefilled_late_fee,
      }));

    if (selectedPeriods.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one period to pay",
        variant: "destructive",
      });
      return;
    }

    try {
      if (data.attestation_answers) {
        await updateAnswersMutation.mutateAsync({
          path: { license_id: licenseData.id },
          body: {
            answers_to_additional_questions: processLicenseAnswers({
              effective_date: licenseData.effective_date,
              ...(licenseData.answers_to_additional_questions ?? {}),
              ...data.attestation_answers,
            } as LicenseAnswers),
          },
        });
      }

      if (paymentMethodType === "no_payment") {
        await invalidateRegistrantDashboardLists(queryClient);
        navigate({
          to: "/registrant/licenses/$licenseId/view",
          params: { licenseId: licenseData.id },
        });
        return;
      }

      if (paymentMethodType === "check") {
        const result = await handleCreateCheckPayment(
          licenseData.id,
          selectedPeriods,
          data.inspection_fee,
        );
        await invalidateRegistrantDashboardLists(queryClient);
        setCheckPaymentSuccess({
          displayId: result.check_id,
          checkMailingAddress: result.check_mailing_address,
          variant: "check",
        });
        return;
      }

      if (paymentMethodType === "invoice") {
        const result = await handleCreateInvoicePayment(
          licenseData.id,
          selectedPeriods,
          data.inspection_fee,
        );
        await invalidateRegistrantDashboardLists(queryClient);
        setCheckPaymentSuccess({
          displayId: result.check_id,
          checkMailingAddress: result.check_mailing_address,
          variant: "invoice",
        });
        return;
      }

      // Step 1: Create pending payment records with user-adjusted amounts
      await handleCreatePendingPayments(
        licenseData.id,
        selectedPeriods,
        data.inspection_fee,
        data.inspection_stripe_fee,
        paymentMethodType === "pay_later" ? true : undefined,
      );

      if (paymentMethodType === "pay_later") {
        await invalidateRegistrantDashboardLists(queryClient);
        navigate({
          to: "/registrant/licenses/$licenseId/view",
          params: { licenseId: licenseData.id },
        });
        return;
      }

      // Step 2: Make payment using those pending records
      if (paymentMethodType) {
        await handleMakePayment(licenseData.id, paymentMethodType);
      }
    } catch (error) {
      void invalidateRegistrantDashboardLists(queryClient);
      toast({
        title: "Error",
        description: `Failed to create payment session: ${getQueryErrorMessage(error)}`,
        variant: "destructive",
      });
    }
  };

  if (!isAuthSetupComplete) {
    return <div>Authenticating...</div>;
  }

  if (isQueryLoading || isFormDataLoading) {
    return <div>Loading payment details...</div>;
  }

  if (
    queryError ||
    formDataError ||
    !feesData ||
    !configurationData ||
    !licenseData
  ) {
    return (
      <div>
        Error loading payment details:{" "}
        {getQueryErrorMessage(queryError || formDataError)}
      </div>
    );
  }

  return (
    <div
      data-slot="registration-form-scroll"
      className="flex min-h-0 flex-1 flex-col overflow-y-auto container mx-auto max-w-3xl"
    >
      <CheckPaymentSuccessDialog
        isOpen={!!checkPaymentSuccess}
        onOpenChange={(open) => {
          if (!open) setCheckPaymentSuccess(null);
        }}
        displayId={checkPaymentSuccess?.displayId ?? ""}
        checkMailingAddress={checkPaymentSuccess?.checkMailingAddress}
        variant={checkPaymentSuccess?.variant ?? "check"}
        onOk={() => {
          if (checkPaymentSuccess && licenseData?.id) {
            navigate({
              to: "/registrant/licenses/$licenseId/view",
              params: { licenseId: licenseData.id },
              search: { section: "property" },
            });
          }
        }}
      />
      <div className="flex items-center gap-4 mb-6">
        <PageHeader
          title="Complete Payment"
          subtitle="Review and adjust your payment details before proceeding to payment"
          className="flex-1"
          registrantDashboardBackSearch={{ openLicense: licenseId }}
          action={<ContextHelpButton articleId="payments" />}
        />
      </div>

      <PaymentPeriodsAndFeesForm
        onSubmit={handleSubmit}
        stepNumber={stepNumber}
        feesData={feesData}
        configurationData={configurationData}
        isRenewal={true}
        initialPaymentMethod={searchPaymentMethod}
        effectiveDateLabel={licenseData.license_type.effective_date_label}
        paymentScreenQuestions={questionsData?.questions}
        paymentScreenAnswers={licenseData.answers_to_additional_questions}
      />
    </div>
  );
}
